import { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
  NodeChange,
  EdgeChange,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Save,
  Play,
  Loader2,
  FolderOpen,
  Sparkles,
} from 'lucide-react';
import DesktopOnlyOverlay from '@/components/layout/DesktopOnlyOverlay';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { NodePanel } from '@/components/workflow/NodePanel';
import { NodeConfigModal } from '@/components/workflow/NodeConfigModal';
import { TriggerNode, ActionNode, FlowControlNode, MCPNode } from '@/components/workflow/WorkflowNodes';
import { AIWorkflowBuilderChatModal } from '@/components/workflow/AIWorkflowBuilderChatModal';
import type { WorkflowEditorDefinition } from '@easyflow/shared';

interface WorkflowData {
  id?: string;
  name: string;
  description: string;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  nodes: Node[];
  edges: Edge[];
  is_active: boolean;
  version: number;
}

// JSON-schema field fragment: only the properties the FE reads are typed;
// the schema stays permissive via the index signature.
interface NodeSchemaField {
  type?: string;
  context?: string;
  description?: string;
  [key: string]: unknown;
}

interface NodeSchema {
  properties?: Record<string, NodeSchemaField>;
  required?: string[];
  [key: string]: unknown;
}

interface NodeDefinition {
  id: string;
  display_name: string;
  description: string;
  category: string;
  icon: string;
  input_schema: NodeSchema;
  output_schema: NodeSchema;
  mcp_server_id?: string;
  mcp_server_name?: string;
}

interface AvailableVariable {
  path: string;
  type: string;
  context?: string;
  source: string;
  source_node_id?: string;
  source_node_label?: string;
  description?: string;
}

export interface WorkflowState {
  workflowId: string | null;
  name: string;
  description: string;
  version: number;
  isActive: boolean;
  isNew: boolean;
  saving: boolean;
  running: boolean;
  nodeCount: number;
  edgeCount: number;
  isDirty: boolean;
  unavailable?: boolean;
  readOnly?: boolean;
}

export interface InitialDraft {
  name: string;
  description?: string;
  trigger_type?: string;
  acts_as_user_ids?: string[];
  allowed_sender_ids?: string[] | null;
  admission_scope?: 'tenant' | 'campaigns';
}

export interface EmbeddedWorkflowBuilderRef {
  save: () => void;
  run: () => void;
  openAIBuilder: () => void;
  resetCanvas: () => void;
  openSelector: () => void;
  getState: () => WorkflowState;
}

interface EmbeddedWorkflowBuilderProps {
  workflowId: string | null;
  initialDraft?: InitialDraft | null;
  onClose?: () => void;
  onRequestOpenSelector?: () => void;
  onSaved?: (workflow: WorkflowData) => void;
  externalToolbar?: boolean;
  onWorkflowStateChange?: (state: WorkflowState) => void;
}

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  flowControl: FlowControlNode,
  mcp: MCPNode,
};

const defaultEdgeOptions = {
  style: { strokeWidth: 2, stroke: '#06b6d4' },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#06b6d4',
  },
  animated: true,
};

const EmbeddedWorkflowBuilderInner = forwardRef<EmbeddedWorkflowBuilderRef, EmbeddedWorkflowBuilderProps>(({ 
  workflowId, 
  initialDraft = null,
  onClose, 
  onRequestOpenSelector,
  onSaved,
  externalToolbar = false,
  onWorkflowStateChange,
}, ref) => {
  const { toast } = useToast();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  const [savedId, setSavedId] = useState<string | null>(null);
  const isNew = !workflowId && !savedId;
  const [definition, setDefinition] = useState<WorkflowEditorDefinition | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [runConfirm, setRunConfirm] = useState(false);
  const requestEpoch = useRef(0);
  const revision = useRef(0);
  const [isDirty, setDirtyState] = useState(false);
  const setIsDirty = useCallback((value: boolean) => {
    if (value) revision.current += 1;
    setDirtyState(value);
  }, []);
  const [showAIBuilderModal, setShowAIBuilderModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveModalName, setSaveModalName] = useState('');
  const [saveModalDesc, setSaveModalDesc] = useState('');

  const [workflow, setWorkflow] = useState<WorkflowData>({
    name: initialDraft?.name || '',
    description: initialDraft?.description || '',
    trigger_type: initialDraft?.trigger_type || 'trigger_manual',
    trigger_config: {},
    nodes: [],
    edges: [],
    is_active: false,
    version: 1,
  });

  const [nodes, setNodes, rfOnNodesChange] = useNodesState([]);
  const [edges, setEdges, rfOnEdgesChange] = useEdgesState([]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    if (changes.some((c) => c.type !== 'select' && (c.type !== 'dimensions' || c.resizing === true))) {
      setIsDirty(true);
    }
    rfOnNodesChange(changes);
  }, [rfOnNodesChange, setIsDirty]);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    if (changes.some((c) => c.type !== 'select')) {
      setIsDirty(true);
    }
    rfOnEdgesChange(changes);
  }, [rfOnEdgesChange, setIsDirty]);
  const [availableNodes, setAvailableNodes] = useState<Record<string, NodeDefinition[]>>({});
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [availableVariables, setAvailableVariables] = useState<AvailableVariable[]>([]);
  const [crmVariables, setCrmVariables] = useState<AvailableVariable[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const handlersRef = useRef<{
    openSaveModal: () => void;
    handleRun: () => void;
    handleOpenSelector: () => void;
  }>({ openSaveModal: () => {}, handleRun: () => {}, handleOpenSelector: () => {} });

  useEffect(() => {
    onWorkflowStateChange?.({
      workflowId: workflowId,
      name: workflow.name,
      description: workflow.description,
      version: workflow.version,
      isActive: workflow.is_active,
      isNew,
      saving,
      running,
      nodeCount: nodes.length,
      edgeCount: edges.length,
      isDirty,
      unavailable: loading || !!loadError,
      readOnly: !!definition && !definition.capabilities.can_edit_definition,
    });
  }, [workflowId, workflow.name, workflow.description, workflow.version, workflow.is_active, isNew, saving, running, nodes.length, edges.length, isDirty, onWorkflowStateChange, loading, loadError, definition]);

  useImperativeHandle(ref, () => ({
    save: () => handlersRef.current.openSaveModal(),
    run: () => handlersRef.current.handleRun(),
    openAIBuilder: () => { if (!loading && !loadError && (!definition || definition.capabilities.can_edit_definition)) setShowAIBuilderModal(true); },
    resetCanvas: () => {
      if (loading || loadError || (definition && !definition.capabilities.can_edit_definition)) return;
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
      setIsDirty(true);
    },
    openSelector: () => handlersRef.current.handleOpenSelector(),
    getState: () => ({
      workflowId: workflowId,
      name: workflow.name,
      description: workflow.description,
      version: workflow.version,
      isActive: workflow.is_active,
      isNew,
      saving,
      running,
      nodeCount: nodes.length,
      edgeCount: edges.length,
      isDirty,
      unavailable: loading || !!loadError,
      readOnly: !!definition && !definition.capabilities.can_edit_definition,
    }),
  }), [workflowId, workflow.name, workflow.description, workflow.version, workflow.is_active, isNew, saving, running, nodes.length, edges.length, isDirty, setNodes, setEdges, setIsDirty, loading, loadError, definition]);

  useEffect(() => {
    const loadCrmVariables = async () => {
      try {
        interface RegistryVariable {
          key: string;
          dataType?: string;
          context?: string;
          description?: string;
        }
        interface RegistryCategory {
          category: string;
          variables?: RegistryVariable[];
        }
        const response = await apiClient.get<{ data?: RegistryCategory[] }>('/registry/variables');
        // Boundary: apiClient returns the raw JSON body ({ data: [...] }) typed as ApiResponse.
        const categories: RegistryCategory[] = (response as { data?: RegistryCategory[] })?.data || [];
        const crmVars: AvailableVariable[] = [];
        for (const cat of categories) {
          for (const v of cat.variables || []) {
            crmVars.push({
              path: v.key,
              type: v.dataType || 'string',
              context: v.context || 'generic_string',
              source: 'crm',
              source_node_label: cat.category,
              description: v.description,
            });
          }
        }
        setCrmVariables(crmVars);
        console.log('[Workflow] Loaded CRM variables from registry:', crmVars.length);
      } catch (error) {
        console.warn('[Workflow] Failed to load CRM variables:', error);
      }
    };
    loadCrmVariables();
  }, []);

  const loadAvailableNodes = async () => {
    try {
      const response = await apiClient.get<{ nodes?: Record<string, NodeDefinition[]> }>('/workflows/available-nodes');
      // Boundary: apiClient returns the raw JSON body ({ nodes } or { data: { nodes } }) typed as ApiResponse.
      const body = response as { nodes?: Record<string, NodeDefinition[]>; data?: { nodes?: Record<string, NodeDefinition[]> } };
      const nodes = body?.data?.nodes || body?.nodes || {};
      console.log('[Workflow] Loaded available nodes:', Object.keys(nodes), 'total categories');
      setAvailableNodes(nodes);
    } catch (error) {
      console.error('Error loading available nodes:', error);
    }
  };

  const normalizeNodeType = (node: Node): Node => {
    const category = node.data?.category || '';
    const nodeType = node.data?.nodeType || node.type || '';
    
    let normalizedType = 'action';
    
    if (category === 'triggers' || 
        nodeType.startsWith('trigger_') || 
        ['whatsapp_received', 'sms_received', 'call_ended', 'call_answered', 
         'voicemail_received', 'lead_created', 'lead_stage_changed', 
         'customer_created', 'form_submitted', 'tag_added', 'webhook'].includes(nodeType)) {
      normalizedType = 'trigger';
    }
    else if (category === 'flow_control' || 
             ['if_condition', 'switch', 'loop', 'delay', 'end_workflow', 'goto', 'parallel'].includes(nodeType)) {
      normalizedType = 'flowControl';
    }
    else if (category.startsWith('mcp:') || node.data?.mcp_server_id || node.data?.mcp_server_name) {
      normalizedType = 'mcp';
    }
    
    return { ...node, type: normalizedType };
  };

  const loadWorkflow = useCallback(async (id: string, metadataOnly = false) => {
    const epoch = ++requestEpoch.current;
    setLoadError(null);
    try {
      setLoading(true);
      const wf = await apiClient.getWorkflowDefinition(id);
      if (epoch === requestEpoch.current) {
        setDefinition(wf);
        if (!metadataOnly) {
          setWorkflow({ ...wf, description: wf.description ?? '' });
          const normalizedNodes = (wf.nodes || []).map(normalizeNodeType);
          setNodes(normalizedNodes);
          setEdges(wf.edges || []);
          setIsDirty(false);
        }
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile caricare il workflow',
        variant: 'destructive',
      });
      if (epoch === requestEpoch.current) setLoadError('Impossibile caricare la definizione. Salvataggio ed esecuzione sono bloccati.');
    } finally {
      if (epoch === requestEpoch.current) setLoading(false);
    }
  }, [setEdges, setNodes, toast, setIsDirty]);
  useEffect(() => () => { ++requestEpoch.current; }, []);

  useEffect(() => {
    loadAvailableNodes();
  }, []);

  useEffect(() => {
    if (!isNew && workflowId) {
      loadWorkflow(workflowId);
    } else if (isNew) {
      setWorkflow({
        name: initialDraft?.name || '',
        description: initialDraft?.description || '',
        trigger_type: initialDraft?.trigger_type || 'trigger_manual',
        trigger_config: {},
        nodes: [],
        edges: [],
        is_active: false,
        version: 1,
      });
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
      setIsDirty(false);
      setLoading(false);
    }
  }, [workflowId, isNew, loadWorkflow, setEdges, setNodes, initialDraft, setIsDirty]);

  // Auto-seed initial trigger node on canvas for newly created workflows.
  // Fires once availableNodes is loaded and only if the canvas is empty.
  const seededTriggerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isNew) {
      seededTriggerRef.current = null;
      return;
    }
    const triggerType = initialDraft?.trigger_type;
    if (!triggerType) return;
    const seedKey = `${triggerType}::${initialDraft?.name || ''}`;
    if (seededTriggerRef.current === seedKey) return;
    if (Object.keys(availableNodes).length === 0) return;
    if (nodes.length > 0) return;

    const triggerDef = availableNodes['triggers']?.find((n) => n.id === triggerType);
    if (!triggerDef) {
      console.warn('[EmbeddedWB] trigger seed: definition not found for', triggerType);
      seededTriggerRef.current = seedKey;
      return;
    }

    const seedNode: Node = {
      id: `${triggerType}_${Date.now()}`,
      type: 'trigger',
      position: { x: 240, y: 200 },
      data: {
        label: triggerDef.display_name,
        nodeType: triggerType,
        config: {},
        icon: triggerDef.icon,
        category: 'triggers',
        input_schema: triggerDef.input_schema,
      },
    };
    setNodes([seedNode]);
    setIsDirty(false);
    seededTriggerRef.current = seedKey;
    console.log('[EmbeddedWB] trigger seed: added', triggerType, 'on canvas');
  }, [isNew, initialDraft, availableNodes, nodes.length, setNodes, setIsDirty]);

  const loadVariablesForNode = async (id: string, nodeId: string) => {
    try {
      const response = await apiClient.get<{ variables: AvailableVariable[] }>(`/workflows/${id}/variables?nodeId=${nodeId}`);
      // Boundary: apiClient returns the raw JSON body ({ variables }) typed as ApiResponse.
      setAvailableVariables((response as { variables?: AvailableVariable[] }).variables || []);
    } catch (error) {
      console.error('Error loading variables:', error);
    }
  };

  useEffect(() => {
    if (selectedNode) {
      if (workflow.id) {
        loadVariablesForNode(workflow.id, selectedNode.id);
      } else if (Object.keys(availableNodes).length > 0) {
        const localVariables: AvailableVariable[] = [
          { path: 'workflow_id', type: 'string', context: 'workflow_id', source: 'system', source_node_label: 'Sistema', description: 'ID workflow' },
          { path: 'run_id', type: 'string', context: 'run_id', source: 'system', source_node_label: 'Sistema', description: 'ID esecuzione' },
          { path: 'timestamp', type: 'date', context: 'datetime', source: 'system', source_node_label: 'Sistema', description: 'Timestamp corrente' },
        ];
        
        const selectedNodeType = selectedNode.data?.nodeType;
        const isTriggerNode = selectedNodeType?.startsWith('trigger_');
        const triggerTypeToUse = isTriggerNode ? selectedNodeType : workflow.trigger_type;
        
        const triggerNodeDef = availableNodes['triggers']?.find(n => n.id === triggerTypeToUse);
        
        if (triggerNodeDef?.output_schema?.properties) {
          Object.entries(triggerNodeDef.output_schema.properties).forEach(([key, schema]) => {
            localVariables.push({
              path: `trigger.${key}`,
              type: schema.type || 'string',
              context: schema.context,
              source: 'trigger',
              source_node_id: workflow.trigger_type,
              source_node_label: triggerNodeDef.display_name || 'Trigger',
              description: schema.description || key
            });
          });
        }
        
        const selectedNodeIndex = nodes.findIndex(n => n.id === selectedNode.id);
        nodes.forEach((node, idx) => {
          if (idx < selectedNodeIndex && node.data?.nodeType) {
            for (const category of Object.values(availableNodes)) {
              const nodeDef = category.find(nd => nd.id === node.data.nodeType);
              if (nodeDef?.output_schema?.properties) {
                Object.entries(nodeDef.output_schema.properties).forEach(([key, schema]) => {
                  localVariables.push({
                    path: `${node.id}.${key}`,
                    type: schema.type || 'string',
                    context: schema.context,
                    source: 'node',
                    source_node_id: node.id,
                    source_node_label: node.data.label || nodeDef.display_name || node.id,
                    description: schema.description || key
                  });
                });
                break;
              }
            }
          }
        });
        
        const allVariables = [...localVariables, ...crmVariables];
        setAvailableVariables(allVariables);
      }
    }
  }, [selectedNode, workflow.id, workflow.trigger_type, nodes, availableNodes, crmVariables]);

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds));
      setIsDirty(true);
    },
    [setEdges, setIsDirty]
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const getNodeType = (category: string): string => {
    if (category === 'triggers') return 'trigger';
    if (category === 'flow_control') return 'flowControl';
    if (category.startsWith('mcp:')) return 'mcp';
    return 'action';
  };

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const nodeData = event.dataTransfer.getData('application/reactflow');
      console.log('[EmbeddedWB] onDrop fired, hasData=', !!nodeData);
      if (!nodeData) return;

      const parsedNode = JSON.parse(nodeData) as NodeDefinition;
      console.log('[EmbeddedWB] dropping node=', parsedNode.id, 'at', event.clientX, event.clientY);

      // React Flow v11+: screenToFlowPosition accetta direttamente le
      // coordinate schermo (clientX/clientY), non serve sottrarre i bounds.
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: Node = {
        id: `${parsedNode.id}_${Date.now()}`,
        type: getNodeType(parsedNode.category),
        position,
        data: {
          label: parsedNode.display_name,
          nodeType: parsedNode.id,
          config: {},
          icon: parsedNode.icon,
          category: parsedNode.category,
          mcp_server_name: parsedNode.mcp_server_name,
          input_schema: parsedNode.input_schema,
        },
      };

      setNodes((nds) => [...nds, newNode]);
      setIsDirty(true);
    },
    [screenToFlowPosition, setNodes, setIsDirty]
  );

  const handleAIGenerateWorkflow = (aiNodes: Node[], aiEdges: Edge[]) => {
    setNodes(aiNodes);
    setEdges(aiEdges);
    setSelectedNode(null);
    setIsDirty(true);
    setShowAIBuilderModal(false);
  };

  const openSaveModal = () => {
    if (loading || loadError || saving || (definition && !definition.capabilities.can_edit_definition)) return;
    setSaveModalName(workflow.name || '');
    setSaveModalDesc(workflow.description || '');
    setShowSaveModal(true);
  };

  const handleSave = async () => {
    if (loading || loadError || saving || (definition && !definition.capabilities.can_edit_definition)) return;
    if (!saveModalName.trim()) {
      toast({
        title: 'Errore',
        description: 'Inserisci un nome per il workflow',
        variant: 'destructive',
      });
      return;
    }

    const savingRevision = revision.current;
    const epoch = requestEpoch.current;
    try {
      setSaving(true);

      const updatedWorkflow = {
        ...workflow,
        name: saveModalName.trim(),
        description: saveModalDesc.trim(),
      };
      setWorkflow(updatedWorkflow);

      const payload: Record<string, unknown> = {
        name: updatedWorkflow.name,
        description: updatedWorkflow.description,
        trigger_type: updatedWorkflow.trigger_type,
        trigger_config: updatedWorkflow.trigger_config,
        nodes,
        edges,
        ...(isNew && initialDraft?.admission_scope ? { admission_scope: initialDraft.admission_scope } : {}),
      };

      if (initialDraft?.acts_as_user_ids !== undefined) {
        payload.acts_as_user_ids = initialDraft.acts_as_user_ids;
      }
      if (initialDraft?.allowed_sender_ids !== undefined) {
        payload.allowed_sender_ids = initialDraft.allowed_sender_ids;
      }
      const savedWorkflow = await apiClient.saveWorkflowDefinition(workflow.id ?? workflowId ?? savedId, payload);
      if (epoch !== requestEpoch.current) return;
      setSavedId(savedWorkflow.id);
      setWorkflow(previous => ({ ...previous, id: savedWorkflow.id, version: savedWorkflow.version }));
      if (isNew) {
        try { setDefinition(await apiClient.getWorkflowDefinition(savedWorkflow.id)); }
        catch { setLoadError('La definizione è stata creata ma non è stato possibile rileggere lo stato autorevole. Riprova prima di modificarla o eseguirla.'); }
      }
      if (epoch !== requestEpoch.current) return;

      toast({
        title: 'Workflow salvato',
        description: `Versione v${savedWorkflow.version} salvata.${revision.current !== savingRevision ? ' Le modifiche successive sono ancora in bozza.' : ''}`,
      });

      if (revision.current === savingRevision) { setIsDirty(false); setShowSaveModal(false); }
      onSaved?.({ ...savedWorkflow, description: savedWorkflow.description ?? '' });
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile salvare il workflow',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRun = () => {
    if (loading || loadError || !workflow.id) return;
    setRunConfirm(true);
  };
  const executeSavedVersion = async () => {
    if (loading || loadError || definition?.admission_scope !== 'tenant' || running) return;
    if (!workflow.id) {
      toast({
        title: 'Salva prima',
        description: 'Devi salvare il workflow prima di eseguirlo',
        variant: 'destructive',
      });
      return;
    }

    try {
      setRunning(true);
      const response = await apiClient.runSavedWorkflow(workflow.id);
      setRunConfirm(false);
      toast({
        title: 'Workflow avviato',
        description: `Run ID: ${response.run_id}`,
      });
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile avviare il workflow',
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  const updateNodeConfig = useCallback((nodeId: string, config: Record<string, unknown>) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, config } } : node
      )
    );
    if (selectedNode && selectedNode.id === nodeId) {
      setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, config } } : null);
    }
    setIsDirty(true);
  }, [setNodes, selectedNode, setIsDirty]);

  const updateNodeLabel = useCallback((nodeId: string, label: string) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, label } } : node
      )
    );
    if (selectedNode && selectedNode.id === nodeId) {
      setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, label } } : null);
    }
    setIsDirty(true);
  }, [setNodes, selectedNode, setIsDirty]);

  const deleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }
    setIsDirty(true);
  }, [setNodes, setEdges, selectedNode, setIsDirty]);

  const handleOpenSelector = () => {
    console.log('[WB-EntryFlow] EmbeddedWB → request open selector');
    onRequestOpenSelector?.();
  };

  handlersRef.current = { openSaveModal, handleRun, handleOpenSelector };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-card/80 backdrop-blur rounded-lg border">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }
  if (loadError) return <div role="alert" className="p-6 space-y-3"><p>{loadError}</p>
    <Button variant="outline" onClick={() => { const id = workflowId ?? savedId; if (id) void loadWorkflow(id, !workflowId && !!savedId); }}>Riprova caricamento</Button></div>;

  return (
    <>
      <div className="flex flex-col h-full w-full" data-workflow-canvas>
        <p className="text-xs p-2 border-b">{definition?.managed_by_org_id ? 'Definizione dell’insegna: sola consultazione.' : 'Definizione condivisa: salvare modifica anche il comportamento futuro delle altre campagne collegate.'}</p>
        <div className="flex flex-1 overflow-hidden rounded-lg border bg-card/80 backdrop-blur">
          {(!definition || definition.capabilities.can_edit_definition) && <NodePanel availableNodes={availableNodes} />}
          
          <div ref={reactFlowWrapper} className="flex-1 relative">
            <ReactFlow
              nodes={nodes}
              nodesDraggable={!definition || definition.capabilities.can_edit_definition}
              nodesConnectable={!definition || definition.capabilities.can_edit_definition}
              elementsSelectable={!definition || definition.capabilities.can_edit_definition}
              edges={edges}
              onNodesChange={!definition || definition.capabilities.can_edit_definition ? onNodesChange : undefined}
              onEdgesChange={!definition || definition.capabilities.can_edit_definition ? onEdgesChange : undefined}
              onConnect={!definition || definition.capabilities.can_edit_definition ? onConnect : undefined}
              onNodeClick={!definition || definition.capabilities.can_edit_definition ? onNodeClick : undefined}
              onPaneClick={onPaneClick}
              onDragOver={onDragOver}
              onDrop={!definition || definition.capabilities.can_edit_definition ? onDrop : undefined}
              nodeTypes={nodeTypes}
              defaultEdgeOptions={defaultEdgeOptions}
              defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
              minZoom={0.3}
              maxZoom={1.5}
              className="bg-muted/30"
            >
              <Background className="text-muted-foreground/20" gap={20} />
              <Controls className="bg-card border" />
              <MiniMap 
                className="bg-card border"
                nodeColor={(node) => {
                  if (node.type === 'trigger') return '#06b6d4';
                  if (node.type === 'flowControl') return '#a855f7';
                  if (node.type === 'mcp') return '#f59e0b';
                  const cat = (node.data?.category || '').toLowerCase();
                  const nt = (node.data?.nodeType || '').toLowerCase();
                  if (cat === 'triggers') return '#06b6d4';
                  if (cat === 'flow_control') return '#a855f7';
                  if (cat === 'messaging') return '#3b82f6';
                  if (cat === 'crm') return '#f97316';
                  if (cat === 'ai' || nt.startsWith('ai_') || nt.startsWith('claude_') || nt.startsWith('gemini_')) return '#8b5cf6';
                  if (cat === 'video_call' || nt.includes('videocall')) return '#6366f1';
                  if (cat === 'supplychain') return '#10b981';
                  if (cat === 'hr') return '#f43f5e';
                  if (cat === 'utilities') return '#22c55e';
                  return '#22c55e';
                }}
              />
            </ReactFlow>

            {!externalToolbar && (!definition || definition.capabilities.can_edit_definition) && (
              <>
                <div className="absolute top-3 left-3 z-10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenSelector}
                    className="bg-card/90 backdrop-blur border shadow-md hover:bg-card gap-2"
                  >
                    <FolderOpen className="h-4 w-4" />
                    {workflow.name || 'Seleziona Workflow'}
                    {workflow.id && (
                      <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">
                        v{workflow.version}
                      </Badge>
                    )}
                    {workflow.is_active && (
                      <Badge className="ml-1 text-[10px] px-1.5 bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30">
                        Definizione attiva
                      </Badge>
                    )}
                  </Button>
                </div>

                <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                  <Button
                    onClick={() => setShowAIBuilderModal(true)}
                    size="icon"
                    className="h-9 w-9 bg-purple-600 hover:bg-purple-700 shadow-md"
                    title="AI Workflow Builder - Crea workflow con l'assistente AI"
                  >
                    <Sparkles className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={handleRun}
                    disabled={running || isNew}
                    size="icon"
                    className={`h-9 w-9 shadow-md ${isNew ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                    aria-label="Esegui la versione salvata"
                    title={isNew ? 'Salva il workflow prima di eseguirlo' : 'Esegui la versione salvata'}
                  >
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  </Button>
                  <Button
                    onClick={openSaveModal}
                    disabled={saving}
                    size="icon"
                    className="h-9 w-9 bg-primary hover:bg-primary/90 shadow-md"
                    title="Salva workflow"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  </Button>
                </div>
              </>
            )}
          </div>

          <NodeConfigModal
            open={!!selectedNode}
            node={selectedNode}
            edges={edges}
            availableNodes={availableNodes}
            availableVariables={availableVariables}
            onUpdateConfig={(config) => selectedNode && updateNodeConfig(selectedNode.id, config)}
            onUpdateLabel={(label) => selectedNode && updateNodeLabel(selectedNode.id, label)}
            onDelete={() => selectedNode && deleteNode(selectedNode.id)}
            onClose={() => setSelectedNode(null)}
          />
        </div>
      </div>

      <Dialog open={runConfirm} onOpenChange={setRunConfirm}>
        <DialogContent><DialogHeader><DialogTitle>Esegui la versione salvata</DialogTitle>
          <DialogDescription>È un’esecuzione reale, non una simulazione. Può inviare messaggi e produrre effetti esterni. La bozza nel canvas non viene eseguita.</DialogDescription></DialogHeader>
          {definition?.admission_scope !== 'tenant' && <p role="alert">Avvio manuale impedito: manca un contesto campagna verificabile oppure l’ambito non è confermato. Usa gli eventi reali della campagna, senza inventare un contesto.</p>}
          <DialogFooter><Button variant="outline" onClick={() => setRunConfirm(false)}>Annulla</Button>
            <Button disabled={running || definition?.admission_scope !== 'tenant'} onClick={executeSavedVersion}>Conferma esecuzione reale</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showSaveModal} onOpenChange={open => { if (!saving) setShowSaveModal(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-primary" />
              Conferma salvataggio
            </DialogTitle>
            <DialogDescription>
              {isNew
                ? 'Verifica i dati e conferma per salvare il workflow.'
                : 'Verifica le modifiche e conferma per aggiornare il workflow.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="save-workflow-name">Nome *</Label>
              <Input
                id="save-workflow-name"
                placeholder="Nome del workflow…"
                value={saveModalName}
                onChange={(e) => { setSaveModalName(e.target.value); setIsDirty(true); }}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="save-workflow-desc">Descrizione</Label>
              <Textarea
                id="save-workflow-desc"
                placeholder="Descrizione (opzionale)…"
                value={saveModalDesc}
                onChange={(e) => { setSaveModalDesc(e.target.value); setIsDirty(true); }}
                rows={3}
              />
            </div>
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
              <span>{nodes.length} {nodes.length === 1 ? 'nodo' : 'nodi'}</span>
              <span>·</span>
              <span>{edges.length} {edges.length === 1 ? 'connessione' : 'connessioni'}</span>
              <span>·</span>
              <span>
                {isNew
                  ? 'Nuova versione v1'
                  : `v${workflow.version} → v${workflow.version + 1}`}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveModal(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={!saveModalName.trim() || saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Conferma e salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AIWorkflowBuilderChatModal
        open={showAIBuilderModal}
        onOpenChange={setShowAIBuilderModal}
        onGenerateWorkflow={handleAIGenerateWorkflow}
        currentWorkflow={{
          name: workflow.name,
          description: workflow.description,
          triggerType: workflow.trigger_type,
          isNew,
          nodes,
          edges,
        }}
      />
    </>
  );
});

EmbeddedWorkflowBuilderInner.displayName = 'EmbeddedWorkflowBuilderInner';

export const EmbeddedWorkflowBuilder = forwardRef<EmbeddedWorkflowBuilderRef, EmbeddedWorkflowBuilderProps>((props, ref) => {
  return (
    <ReactFlowProvider>
      <DesktopOnlyOverlay
        title="Workflow Builder"
        description="L'editor visuale dei workflow richiede uno schermo più grande. Aprilo da tablet o desktop, oppure forza la visualizzazione."
      >
        <EmbeddedWorkflowBuilderInner ref={ref} {...props} />
      </DesktopOnlyOverlay>
    </ReactFlowProvider>
  );
});

EmbeddedWorkflowBuilder.displayName = 'EmbeddedWorkflowBuilder';
